import Extension from "../base/extension";
import debounce from "../utilities/debounce";

class Facets extends Extension {
  form; // The HTML form element of the facets form
  filterData = []; // Cache of previous filter view requests, so we only fetch a given filter view once
  searchParamsInitial;
  searchParamsPrev;

  /**
   * ID of the section, so we know what section to request via fetch
   * @type {String}
   */
  sectionId;

  constructor(name) {
    super(name);
  }

  /**
   * Initialisation
   */
  init() {
    // Detect changes of radios within this element, as that means variant changed
    this.form = document.querySelector("#facets-form");
    if(!this.form) return;

    this.searchParamsInitial = window.location.search.slice(1);
    this.searchParamsPrev = window.location.search.slice(1);
    const products_container = document.querySelector("#products-container");
    if (!(products_container instanceof HTMLElement)) return;
    this.sectionId = products_container.dataset.sectionId;

    this.setupEventListeners();
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Listen for form input changes - i.e. the facet inputs. We call the
    // listener after a 'debounce' period to allow the user some time to
    // complete their input (rather than immediately on input)
    this.debouncedOnInput = debounce((event) => {
      this.onFacetsFormInputChange(event);
    }, 500);
    this.form.addEventListener('input', (event) => {
      if (this.shouldDeferInlineFacetSubmit(event.target)) return;
      this.debouncedOnInput(event);
    });

    // Listen for 'facet remove' clicks, trigger the load. We need to listen on
    // the container, rather than directly on the buttons, as the buttons are
    // re-rendered / dynamically injected each time a filter is applied.
    this.form.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;

      if (target?.closest('.facet-apply')) {
        event.preventDefault();
        this.closeInlineFacetDropdown(target.closest('.facet-apply'));
        this.onFacetsFormInputChange(event);
        return;
      }

      const link = target?.closest('a');
      if (link?.classList.contains('facet-remove')) {
        event.preventDefault();
        const mockedEvent = {
          currentTarget: link,
          preventDefault: () => {}
        };
        this.onActiveFilterClick(mockedEvent);
      }
    });
  }

  /**
   * Inline facet dropdowns defer submission until Apply is clicked so users
   * can select multiple values without triggering a fetch on each checkbox.
   */
  shouldDeferInlineFacetSubmit(target) {
    return target instanceof Element && Boolean(target.closest('.facet-dropdown-menu'));
  }

  closeInlineFacetDropdown(applyButton) {
    const toggle = applyButton?.closest('.btn-group')?.querySelector('[data-bs-toggle="dropdown"]');
    if (toggle && window.bootstrap?.Dropdown) {
      window.bootstrap.Dropdown.getOrCreateInstance(toggle).hide();
    }
  }

  /**
   * Handle an input change in the facets form, and render the new results
   * @param {Event} e input event 
   */
  onFacetsFormInputChange(e) {
    const formData = new FormData(this.form);
    const searchParams = new URLSearchParams(formData).toString();
    this.renderPage(searchParams, e);
  }

  /**
   * Render the section
   * 
   * @param {String} searchParams The query string to load (filters / page)
   * @param {Event} event The original trigger event
   * @param {Boolean} updateURLHash Whether to update the URL hash with the new URL
   */
  renderPage(searchParams, event, updateURLHash = true) {
    this.searchParamsPrev = searchParams;
    document.getElementById('results-loading-faceted-results').classList.remove('d-none');
    const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;
    
    // Check if we have this combo of filters in the cache. If so, use cache. If
    // not, fetch fresh from Shopify.
    const filterDataUrl = (element) => element.url === url;
    this.filterData.some(filterDataUrl) ? 
      this.renderSectionFromCache(filterDataUrl, event) 
      : this.renderSectionFromFetch(url, event);

    if (updateURLHash) {
      this.updateURLHash(searchParams);
    }
  }

  /**
   * Render the section from previously-stored fetch (i.e. cache)
   * 
   * @param {(entry: any) => boolean} filterDataUrl Predicate to select cached entry
   * @param {Event} event 
   */
  renderSectionFromCache(filterDataUrl, event) {
    const cached_result = this.filterData.find(filterDataUrl);
    if (!cached_result) return;
    const html = cached_result.html;
    this.renderFilters(html, event);
    this.renderProductGridContainer(html);
    this.renderPagination(html);
    document.getElementById('results-loading-faceted-results').classList.add('d-none');
    this.runPostRenderHooks();
    this.dispatchCollectionFiltered();
  }

  /**
   * Render the section by fetching it fresh
   * 
   * @param {String} url 
   * @param {Event} event 
   */
  renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;

        // Cache this response so we can load directly if the same options are
        // picked.
        this.filterData = [...this.filterData, { html, url }];

        this.renderFilters(html, event);
        this.renderProductGridContainer(html);
        this.renderPagination(html);
        document.getElementById('results-loading-faceted-results').classList.add('d-none');
        this.runPostRenderHooks();
        this.dispatchCollectionFiltered();
      });
  }

  runPostRenderHooks() {
    const flashtag_loader = globalThis['flashtagLoader'];
    if (typeof flashtag_loader === 'function') {
      flashtag_loader();
    }

    if (typeof Waypoint !== 'undefined' && typeof Waypoint.refreshAll === 'function') {
      Waypoint.refreshAll();
    }
  }

  dispatchCollectionFiltered() {
    document.dispatchEvent(new CustomEvent('collection:filtered'));
  }

  /**
   * Update the URL hash with new parameters
   * 
   * @param {String} searchParams 
   */
  updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  /**
   * Extract and replace the facet (filter dropdowns) HTML
   * 
   * @param {String} html
   * @param {Event} event
   */
  renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetLiveRegions = parsedHTML.querySelectorAll("[data-live-region^='facet-']");

    Array.from(facetLiveRegions).forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      const liveRegionId = element.dataset.liveRegion;
      if (this.shouldSkipFacetRerender(event, liveRegionId)) return;

      const live_region = document.querySelector(
        `[data-live-region="${liveRegionId}"]`
      );
      if (live_region instanceof HTMLElement) {
        live_region.innerHTML = element.innerHTML;
      }
    });

    this.updateFacetToggleLabels(parsedHTML, event);

    // Update HTML of the 'active filters' section
    this.renderActiveFacets(parsedHTML);
  }

  /**
   * Preserve in-progress checkbox selections by not re-rendering the open
   * inline facet while the user is still editing inside its dropdown.
   */
  shouldSkipFacetRerender(event, liveRegionId) {
    if (!event?.target || !(event.target instanceof Element)) return false;
    if (event.target.closest('.facet-apply')) return false;
    if (!event.target.closest('.facet-dropdown-menu')) return false;

    const facet = event.target.closest('[data-facet-index]');
    if (!(facet instanceof HTMLElement)) return false;

    return liveRegionId === `facet-${facet.dataset.facetIndex}-body`;
  }

  updateFacetToggleLabels(parsedHTML, event) {
    const activeFacetIndex = event?.target instanceof Element
      ? event.target.closest('[data-facet-index]')?.dataset?.facetIndex
      : null;

    parsedHTML.querySelectorAll('[data-facet-index]').forEach((facet) => {
      if (!(facet instanceof HTMLElement)) return;
      if (facet.dataset.facetIndex === activeFacetIndex && this.shouldSkipFacetRerender(event, `facet-${facet.dataset.facetIndex}-body`)) {
        return;
      }

      const existing = document.querySelector(`[data-facet-index="${facet.dataset.facetIndex}"]`);
      const newToggle = facet.querySelector('[data-bs-toggle="dropdown"]');
      const existingToggle = existing?.querySelector('[data-bs-toggle="dropdown"]');
      if (newToggle && existingToggle) {
        existingToggle.innerHTML = newToggle.innerHTML;
      }
    });
  }

  /**
   * Extract and replace the active facet (filter badges) HTML
   * 
   * @param {Document} html
   */
  renderActiveFacets(html) {
    const activeFacetElementSelectors = ['#facets-active'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const existing_element = document.querySelector(selector);
      if (existing_element instanceof HTMLElement) {
        existing_element.outerHTML = activeFacetsElement.outerHTML;
      }
    });
    //FacetFiltersForm.toggleActiveFacets(false);
  }

  /**
   * Render the results products grid
   * 
   * @param {String} html
   */
  renderProductGridContainer(html) {
    const results_grid = document.getElementById('results-grid');
    const parsed_grid = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('results-grid');
    if (results_grid instanceof HTMLElement && parsed_grid instanceof HTMLElement) {
      results_grid.innerHTML = parsed_grid.innerHTML;
    }
  }

  /**
   * Extract and replace the pagination HTML
   * 
   * @param {String} html
   */
  renderPagination(html) {
    const pagination = document.getElementById('pagination');
    const parsed_pagination = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('pagination');
    if (pagination instanceof HTMLElement && parsed_pagination instanceof HTMLElement) {
      pagination.outerHTML = parsed_pagination.outerHTML;
    }
  }

  /**
   * Start the process of applying a filter
   * 
   * @param {Event} event The initiating event
   */
  onActiveFilterClick(event) {
    event.preventDefault();
    //this.toggleActiveFacets();
    if (!(event.currentTarget instanceof HTMLAnchorElement)) return;
    const href = event.currentTarget.href;
    const url = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
    this.renderPage(url, event);
  }
}

export default Facets;